import { TblCol, TblSection } from '../table.types';
import { TblSectionSpec } from '../spec';
import { LegacyTableView } from '../table-view.types';
import { filterTableByView, findViewById, FilteredColumns, adjustSections } from './columnFilter';

export type GetTableViewColumnsParams = {
    allColumns: TblCol[];
    allSections?: TblSection[];
    views: LegacyTableView[];
    currentViewId: string;
};

export type GetTableViewColumnsWithSpecsParams = {
    allColumns: TblCol[];
    originalSectionSpecs: TblSectionSpec[];
    view?: LegacyTableView;
};

export type GetTableViewColumnsAllParams = GetTableViewColumnsParams | GetTableViewColumnsWithSpecsParams;

export type GetTableViewColumnsReturn = FilteredColumns & {
    currentView: LegacyTableView | null;
    hasViewSelected: boolean;
    isDefaultView: boolean;
};

/**
 * getTableViewColumns
 *
 * Pure function to provide filtered columns and sections for table rendering based on the selected table view.
 * Supports both legacy and UI formats for table views, allowing flexible column visibility and section layout.
 *
 * Usage:
 *   - Pass in all columns, views (legacy format), current view ID, or section specs and a view object.
 *   - Returns filtered columns, sections, and view metadata for rendering.
 */
export function getTableViewColumns(params: GetTableViewColumnsAllParams): GetTableViewColumnsReturn {
    // Handle the traditional approach with views array
    if ('views' in params && 'currentViewId' in params) {
        const { allColumns, allSections, views, currentViewId } = params;
        const currentView = findViewById(views, currentViewId);
        const hasViewSelected = !!currentView;
        const isDefaultView = currentViewId === 'default' || !currentView;

        const filteredResult = filterTableByView(allColumns, allSections, currentView || undefined);

        return {
            ...filteredResult,
            currentView: currentView || null,
            hasViewSelected,
            isDefaultView,
        };
    }

    // Handle the new approach with section specs
    if ('originalSectionSpecs' in params) {
        const { allColumns, originalSectionSpecs, view } = params;
        const hasViewSelected = !!view;
        const isDefaultView = !view;

        if (!view || !view.cols) {
            // Only use columns present in originalSectionSpecs for Default
            const sectionColKeys = originalSectionSpecs.flatMap(section =>
                Array.isArray(section.cols) ? section.cols : [],
            );
            const sectionColKeySet = new Set(sectionColKeys);
            const filteredColumns = allColumns.filter(col => sectionColKeySet.has(col.key));
            return {
                columns: filteredColumns,
                sections: adjustSections(
                    originalSectionSpecs,
                    filteredColumns.map(col => col.key),
                ),
                currentView: null,
                hasViewSelected: false,
                isDefaultView: true,
            };
        }

        // Handle both array and pipe-delimited string formats for view.cols
        let viewColumnKeys: Set<string>;

        if (Array.isArray(view.cols)) {
            viewColumnKeys = new Set(view.cols);
        } else if (typeof view.cols === 'string') {
            // Parse pipe-delimited string (e.g., "|FeedType|Direction|ImportDate|")
            const colsArray = view.cols.split('|').filter(col => col.trim() !== '');
            viewColumnKeys = new Set(colsArray);
        } else {
            // Fallback to all columns if format is unexpected
            viewColumnKeys = new Set(allColumns.map(col => col.key));
        }

        // SCHEMA VALIDATION: Filter out non-existent columns and warn about them
        const availableColumnKeys = new Set(allColumns.map(col => col.key));
        const invalidColumns = Array.from(viewColumnKeys).filter(colKey => !availableColumnKeys.has(colKey));

        if (invalidColumns.length > 0) {
            invalidColumns.forEach(invalidCol => viewColumnKeys.delete(invalidCol));
        }

        const filteredColumns = allColumns.filter(col => viewColumnKeys.has(col.key));
        const filteredSections = adjustSections(
            originalSectionSpecs,
            filteredColumns.map(col => col.key),
        );

        return {
            columns: filteredColumns,
            sections: filteredSections,
            currentView: view,
            hasViewSelected,
            isDefaultView,
        };
    }

    // Should never reach here
    throw new Error('Invalid parameters for getTableViewColumns');
}
